import React, { Component } from "react";
import { FormattedDate, FormattedMessage, FormattedTime, injectIntl } from "react-intl";
import { LAST_MODIFIED_DATE, SURVEY_NAME, SURVEY_STATUS } from "../../../ducks/survey";
import ObjectUtils, { coalesce } from "../../../utils/ObjectUtils";

import {
  faCog,
  faArrowsRotate,
  faMagnifyingGlass,
  faCopy,
  faExclamationTriangle,
  faFileExport,
  faHistory,
  faMagicWandSparkles,
  faPencilAlt,
  faPlayCircle,
  faProjectDiagram,
  faCalendarAlt
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import { Button, ButtonGroup, ButtonToolbar, Dropdown } from "react-bootstrap";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { ADMIN, TEMPLATE_CREATOR } from "../../../common/constant";
import SurveyUtils from "../../../utils/SurveyUtils";
import TrashButton from "../../common/TrashButton";
import ReportTemplateYearDialog from "./ReportTemplateYearDialog";
import SynchronizationPreviewDialog from "./SynchronizationPreviewDialog";

const { opt } = ObjectUtils;

/**
 * The component displays survey templates from the server.
 * When compoment was rendered, we will call server to load data.
 */
class TemplateListTableRows extends Component {
  constructor(props) {
    super(props);
    this.row = this.row.bind(this);
    this.historyRows = this.historyRows.bind(this);
    this.callShowModal = this.callShowModal.bind(this);
    this.activateTemplate = this.activateTemplate.bind(this);
    this.showActivationInfo = this.showActivationInfo.bind(this);
    this.questionSync = this.questionSync.bind(this);
    this.loadTemplate = this.loadTemplate.bind(this);
    this.copySurveyTemplate = this.copySurveyTemplate.bind(this);
    this.setSettingsSurvey = this.setSettingsSurvey.bind(this);
    this.loadHistory = this.loadHistory.bind(this);
    this.templateSyncColumn = this.templateSyncColumn.bind(this);
    this.loadVersionTemplate = this.loadVersionTemplate.bind(this);

    this.state = { showToggle: false, synchronizingVersion: null };
  }

  onSurveyPreview(template, history) {
    const { propertyChange, loadSurveyVersion, previewSurvey } = this.props;

    propertyChange("surveyCurrentPage", "");
    loadSurveyVersion(template, coalesce(history, template), false, false, true);
    previewSurvey();
  }

  render() {
    const { templateList } = this.props;

    return <>
      <tbody>{templateList.map(this.row)}</tbody>
      {this.state.dateTemplate && <ReportTemplateYearDialog
        key={`${this.state.dateTemplate.identifier}-${this.state.dateTemplate.version}`}
        template={this.state.dateTemplate}
        onClose={() => this.setState({ dateTemplate: null })}
      />}
      {this.state.synchronizationPreview && <SynchronizationPreviewDialog
        key={`${this.state.synchronizationPreview.identifier}-${this.state.synchronizationPreview.version}`}
        template={this.state.synchronizationPreview}
        onClose={() => this.setState({ synchronizationPreview: null })}
      />}
    </>;
  }

  dateButton(template, history) {
    return <Button size="sm" variant="link"
      title={this.props.intl.formatMessage({ id: "template.year.edit" })}
      onClick={() => this.setState({ dateTemplate: {
        identifier: template.identifier,
        version: history?.version ?? template.version,
        latestVersion: template.version,
        surveyName: history?.title || template.surveyName
      } })}>
      <FontAwesomeIcon icon={faCalendarAlt} />
    </Button>;
  }

  /**
   * A row renderer
   */
  row(template, i) {
    let { intl, showHistory, sortTableTemplateBy, templateHistory, deleteTemplate, roles, templateList } = this.props;
    let TEMP_CREATOR = roles.includes(TEMPLATE_CREATOR);
    let ADMIN_USER = roles.includes(ADMIN);
    const hasActivatedVersions = template.status === "active" || template.hasActivatedVersions;
    let historyRows =
      showHistory[template.identifier] &&
      (templateHistory[template.identifier] ? templateHistory[template.identifier].length > 0 : false)
        ? this.historyRows(i, template)
        : null;
    const tooltip = template.lastModifiedDate ? (
      <Tooltip className="date-tooltip history-row-tooltip" id="tooltip">
        <FormattedTime value={new Date(template.lastModifiedDate)} hour="numeric" minute="2-digit" second="2-digit" />
      </Tooltip>
    ) : null;
    const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
      <Button
        ref={ref}
        size="sm"
        variant="link"
        onClick={(e) => {
          e.preventDefault();
          onClick(e);
        }}
      >
        {children}
        <FontAwesomeIcon icon={faMagicWandSparkles} />
      </Button>
    ));

    return [
      <tr key={i} className={showHistory[i] && "history"}>
        <td className={SurveyUtils.addClassForTableColumns(SURVEY_NAME, sortTableTemplateBy, "text-long")}>
          {template.surveyName}
        </td>
        <td
          className={`${SurveyUtils.addClassForTableColumns(SURVEY_STATUS, sortTableTemplateBy, "shrink")} overflow-visible`}
        >
          <FormattedMessage
            id={
              template.questionSync
                ? template.questionsSynced
                  ? "templateList.sync.released"
                  : "templateList.sync.unreleased"
                : `templateList.status.${coalesce(template.status, "draft")}`
            }
          />
          {TEMP_CREATOR && (
            <Dropdown className="d-inline" drop={templateList.length < 3 ? "up" : undefined}>
              <Dropdown.Toggle as={CustomToggle} id="dropdown-custom-components" />

              <Dropdown.Menu>
                <Dropdown.Item
                  href="#"
                  onClick={() =>
                    template.questionSync ? this.questionSync(template) : this.activateTemplate(template)
                  }
                >
                  <FormattedMessage id={"templateList.tooltip." + (template.questionSync ? "sync" : "activate")} />
                </Dropdown.Item>
                <Dropdown.Item href="#" onClick={() => {
                  this.showActivationInfo(template)
                }}>
                  <FormattedMessage id="templateList.tooltip.activations.overview" />
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}
        </td>
        {TEMP_CREATOR && (
          <td className="actions">
            <ButtonToolbar>
              <ButtonGroup>
                <Button
                  size="sm"
                  variant="link"
                  title={intl.formatMessage({ id: "templateList.tooltip.edit" })}
                  onClick={() => this.loadTemplate(template)}
                >
                  <FontAwesomeIcon icon={faPencilAlt} />
                </Button>
                {ADMIN_USER && (
                  <Button
                    disabled={!template.version || template.version < 2}
                    size="sm"
                    variant="link"
                    title={intl.formatMessage({ id: "templateList.tooltip.compare" })}
                    onClick={() => this.compareVersions(template, window.history)}
                  >
                    <FontAwesomeIcon icon={faProjectDiagram} />
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="link"
                  disabled={!!template.questionSync}
                  title={intl.formatMessage({ id: "templateList.tooltip.copy" })}
                  onClick={() => this.copySurveyTemplate(template, null)}
                >
                  <FontAwesomeIcon icon={faCopy} />
                </Button>
                <Button
                  size="sm"
                  variant="link"
                  title={intl.formatMessage({ id: "templateList.tooltip.basic.edit" })}
                  onClick={() => this.setSettingsSurvey(template, false)}
                >
                  <FontAwesomeIcon icon={faCog} />
                </Button>
                {ADMIN_USER && this.dateButton(template)}
                {this.synchronizeButton(template)}
                <OverlayTrigger placement="top" overlay={<Tooltip id={`delete-template-${template.identifier}`}>
                  <FormattedMessage id={hasActivatedVersions
                    ? "templateList.tooltip.delete.activatedVersions"
                    : "templateList.tooltip.delete"} />
                </Tooltip>}>
                  <span className="d-inline-block" tabIndex={hasActivatedVersions ? 0 : undefined}>
                    <TrashButton
                      onClick={() =>
                        this.callShowModal(
                          template,
                          deleteTemplate,
                          "dialog.template.delete.title",
                          "dialog.template.delete.text",
                          "dialog.delete",
                          "dialog.dismiss"
                        )
                      }
                      disabled={hasActivatedVersions || !(template.status === "draft" && !template.questionSync)}
                      titleId={hasActivatedVersions
                        ? "templateList.tooltip.delete.activatedVersions"
                        : "templateList.tooltip.delete"}
                    />
                  </span>
                </OverlayTrigger>
              </ButtonGroup>
            </ButtonToolbar>
          </td>
        )}
        <td className={SurveyUtils.addClassForTableColumns(LAST_MODIFIED_DATE, sortTableTemplateBy)}>
          {template.lastModifiedDate && (
            <OverlayTrigger placement="right" overlay={tooltip}>
              <span>
                <FormattedDate
                  value={new Date(template.lastModifiedDate)}
                  year="numeric"
                  month="2-digit"
                  day="2-digit"
                />
              </span>
            </OverlayTrigger>
          )}
        </td>
        <td className={SurveyUtils.addClassForTableColumns("updatedBy", sortTableTemplateBy)}>
          {!template.updatedBy ? "Unbekannte" : template.updatedBy}
        </td>
        <td className="number">
          {template.version}
          <Button
            size="sm"
            variant="link"
            title={intl.formatMessage({ id: "templateList.tooltip.history" })}
            onClick={() => this.loadHistory(i, template, true)}
          >
            <FontAwesomeIcon icon={faHistory} />
          </Button>
        </td>
        <td className={sortTableTemplateBy === "language" ? "sorted" : ""}>
          {coalesce(opt(template.language).name, template.languageId, "")}
        </td>
        <td className="actions">
          {ADMIN_USER && !TEMP_CREATOR && this.dateButton(template)}
          {!TEMP_CREATOR && this.synchronizeButton(template)}
          <Button
            size="sm"
            variant="link"
            onClick={() => this.onSurveyPreview(template)}
            title={intl.formatMessage({ id: "templateList.tooltip.survey.preview" })}
          >
            <FontAwesomeIcon icon={faPlayCircle} />
          </Button>
          <Button
            size="sm"
            variant="link"
            title={intl.formatMessage({ id: "templateList.tooltip.pdf.create" })}
            onClick={() => this.openPDF(template)}
          >
            <FontAwesomeIcon icon={faFileExport} />
          </Button>
        </td>
      </tr>,
      historyRows
    ];
  }

  loadHistory(i, template, showHistory) {
    const { loadHistory } = this.props;
    loadHistory(i, template, showHistory);
  }

  templateSyncColumn(history) {
    return (
      <>
        {history.questionsSynced ? (
          <FormattedMessage id={"templateList.sync.released"} />
        ) : (
          <FormattedMessage id={"templateList.sync.unreleased"} />
        )}
      </>
    );
  }

  historyRows(i, template) {
    const { intl, templateHistory, deleteTemplateVersion, roles } = this.props;
    let TEMP_CREATOR = roles.includes(TEMPLATE_CREATOR);
    return [
      templateHistory[template.identifier].map((history) => {
        if (String(history.version) === String(template.version)) {
          return null;
        }

        const historyTooltip = history.updated ? (
          <Tooltip className="date-tooltip" id="tooltip">
            <FormattedTime value={new Date(history.updated)} hour="numeric" minute="2-digit" second="2-digit" />
          </Tooltip>
        ) : null;
        return (
          <tr key={history.version} className="history">
            <td>{history.status === "active" ? history.title : null}</td>
            <td className="shrink">
              {template.questionSync ? (
                this.templateSyncColumn(history)
              ) : (
                <FormattedMessage id={`templateList.status.${coalesce(history.status, "draft")}`} />
              )}
            </td>
            {TEMP_CREATOR && (
              <td className="actions">
                <Button
                  size="sm"
                  variant="link"
                  title={intl.formatMessage({ id: "templateList.tooltip.edit" })}
                  onClick={() => this.loadVersionTemplate(template, history)}
                >
                  <FontAwesomeIcon icon={faPencilAlt} />
                </Button>
                <Button
                  size="sm"
                  variant="link"
                  title={intl.formatMessage({ id: "templateList.tooltip.copy" })}
                  onClick={() => this.copySurveyTemplate(template, history)}
                >
                  <FontAwesomeIcon icon={faCopy} />
                </Button>
                {roles.includes(ADMIN) && history.status === "active" ? this.dateButton(template, history) : <TrashButton
                  onClick={() =>
                    this.callShowModal(
                      {
                        survey: template,
                        history: history
                      },
                      deleteTemplateVersion,
                      "dialog.template.delete.title",
                      "dialog.template.delete.text",
                      "dialog.delete",
                      "dialog.dismiss"
                    )
                  }
                  disabled={history.status !== "draft"}
                  titleId="templateList.tooltip.delete"
                />}
                {this.synchronizeButton(template, history)}
              </td>
            )}
            <td className="date">
              {!!template.lastModifiedDate && (
                <OverlayTrigger placement="right" overlay={historyTooltip}>
                  <span>
                    <FormattedDate value={new Date(history.updated)} year="numeric" month="2-digit" day="2-digit" />
                  </span>
                </OverlayTrigger>
              )}
            </td>
            <td>{!history.updatedBy ? "admin" : history.updatedBy}</td>
            <td className="number">
              {history.version}
            </td>
            <td></td>
            <td className="actions">
              {roles.includes(ADMIN) && !TEMP_CREATOR && history.status === "active" && this.dateButton(template, history)}
              {!TEMP_CREATOR && this.synchronizeButton(template, history)}
              <Button
                size="sm"
                variant="link"
                title={intl.formatMessage({ id: "templateList.tooltip.survey.preview" })}
                onClick={() => this.onSurveyPreview(template, history)}
              >
                <FontAwesomeIcon icon={faPlayCircle} />
              </Button>
              <Button
                size="sm"
                variant="link"
                onClick={() => this.openPDF(template, history)}
                title={intl.formatMessage({ id: "templateList.tooltip.pdf.create" })}
              >
                <FontAwesomeIcon icon={faFileExport} />
              </Button>
            </td>
          </tr>
        );
      })
    ];
  }

  synchronizeButton(template, version = template) {
    if (this.props.currentUsername !== "daad-support@mainlevel.de" ||
        version.status !== "active" || template.questionSync) {
      return null;
    }
    const label = this.props.intl.formatMessage({ id: "templateList.synchronize.tooltip" });
    const previewLabel = this.props.intl.formatMessage({ id: "templateList.synchronize.preview.tooltip" });
    return (
      <>
      <Button size="sm" variant="link" title={previewLabel} aria-label={previewLabel}
        disabled={this.state.synchronizingVersion !== null}
        onClick={() => this.setState({ synchronizationPreview: {
          identifier: template.identifier, version: version.version,
          surveyName: version.title || template.surveyName
        } })}>
        <FontAwesomeIcon icon={faMagnifyingGlass} />
      </Button>
      <Button
        size="sm"
        variant="link"
        title={label}
        aria-label={label}
        disabled={this.state.synchronizingVersion !== null}
        onClick={() => this.synchronizeVersion(template, version)}
      >
        <FontAwesomeIcon icon={faArrowsRotate}
          spin={this.state.synchronizingVersion === `${template.identifier}:${version.version}`} />
      </Button>
      </>
    );
  }

  async synchronizeVersion(template, history) {
    if (this.props.currentUsername !== "daad-support@mainlevel.de" ||
        history.status !== "active" || this.state.synchronizingVersion !== null) {
      return;
    }
    const key = `${template.identifier}:${history.version}`;
    this.setState({ synchronizingVersion: key });
    try {
      await this.props.synchronizeTemplateVersion(template, history);
    } finally {
      this.setState({ synchronizingVersion: null });
    }
  }

  loadTemplate(template) {
    const { loadSurvey } = this.props;
    //Calls 'ducks' function, that will load the template data from the server.
    loadSurvey(template);
  }

  loadVersionTemplate(template, history) {
    const { loadSurveyVersion } = this.props;
    //Calls 'ducks' function, that will load the template data from the server.
    loadSurveyVersion(template, history);
  }

  compareVersions(template) {
    const { surveyVersionCompare } = this.props;
    surveyVersionCompare(template);
  }

  setSettingsSurvey(template, activate) {
    const { loadSurveySettings, loadReportingPeriods } = this.props;
    //triggers new_survey_event.
    loadSurveySettings(template, activate);
    if (activate) {
      loadReportingPeriods();
    }
  }

  activateTemplate(template) {
    const { activateTemplate } = this.props;
    activateTemplate(template);
  }

  showActivationInfo(template) {
    const { showActivationInfoDialog } = this.props;
    showActivationInfoDialog(template);
  }

  questionSync(template) {
    const { questionSync } = this.props;
    questionSync(template);
  }

  copySurveyTemplate(survey, history) {
    //triggers copy_survey_event.
    const { intl, copySurveyTemplate } = this.props;
    let copySuffix = intl.formatMessage({ id: "app.common.Copy" });
    copySurveyTemplate(survey, copySuffix, history);
  }

  callShowModal(template, deleteTemplate, titleTemplate, textTemplate, deleteLabel, dismissLabel) {
    const { showDialog } = this.props;
    //when user click on delete, we are triggering [ducks].showDialog
    showDialog(
      template,
      deleteTemplate,
      titleTemplate,
      textTemplate,
      deleteLabel,
      dismissLabel,
      <FontAwesomeIcon icon={faExclamationTriangle} size="2x" />
    );
  }

  openPDF(template, history) {
    const { loadSurveyVersion } = this.props;
    //survey, historyVersion, link, push = true , createPDF = false, showPreview=false - default
    loadSurveyVersion(template, coalesce(history, template), false, true);
  }
}

TemplateListTableRows.propTypes = {
  templateList: PropTypes.array,
  deleteTemplate: PropTypes.func,
  loadSurvey: PropTypes.func,
  loadHistory: PropTypes.func,
  loadSurveySettings: PropTypes.func,
  loadReportingPeriods: PropTypes.func,
  copySurveyTemplate: PropTypes.func,
  showActivationInfoDialog:PropTypes.func,
  showDialog: PropTypes.func,
  showHistory: PropTypes.array,
  templateHistory: PropTypes.array,
  sortTableTemplateBy: PropTypes.string,
  loadSurveyVersion: PropTypes.func,
  deleteTemplateVersion: PropTypes.func,
  activateTemplate: PropTypes.func,
  questionSync: PropTypes.func,
  currentUsername: PropTypes.string,
  synchronizeTemplateVersion: PropTypes.func,
  propertyChange: PropTypes.func,
  previewSurvey: PropTypes.func,
  intl: PropTypes.object.isRequired,
  roles: PropTypes.array,
};

export default injectIntl(TemplateListTableRows);
